/**
 * The stage of processing a row of the case's documents shows (contract §4). The server names a
 * stage only for an audio file, whose extraction transcribes it first, and for a video whose run
 * asked for its sound (PRD-0115), whose picture is analysed first. A row says it while
 * the document is on its way, and where it stopped once it failed; a file that was never started
 * or is processed reads by its status alone, as every other document does. Pure.
 */
import {
  isLiveReading,
  readStage,
  type StageMarks,
  stageReading,
} from '@/components/processing/processing-stages'
import type { CaseDocumentResponse } from '@/services/api/cases'
import { isAudioFileType } from '@/utils/audio-playback'
import { isVideoFileType } from '@/utils/video-file'

type StagedDocument = Pick<CaseDocumentResponse, 'file_type' | 'status' | 'stage' | 'failed_stage'>

/** The statuses of a document whose job has started and not ended. */
const IN_FLIGHT_STATUSES: ReadonlySet<string> = new Set(['analyzing', 'scheduled', 'processing'])

/** Whether a job of any kind is on the document (the server's `DOC_INFLIGHT_STATUSES`). */
export function isDocumentInFlight(status: string): boolean {
  return IN_FLIGHT_STATUSES.has(status)
}

/**
 * The stages a row shows, or null. A stage that contradicts the status is not shown: "queued" on
 * a file nobody started (the server reads no job that way), a failed document with no stage it
 * failed in, a stage of a failure on a document that is being processed again.
 */
export function audioStageOf(document: StagedDocument): Required<StageMarks> | null {
  const video = isVideoFileType(document.file_type)
  if (!video && !isAudioFileType(document.file_type)) return null
  const stage = readStage(document.stage)
  const failedStage = readStage(document.failed_stage)
  const reading = stageReading(stage, failedStage, video)
  if (!isLiveReading(reading)) return null
  const fits =
    reading.kind === 'failed'
      ? document.status === 'failed'
      : IN_FLIGHT_STATUSES.has(document.status)
  return fits ? { stage, failedStage, video } : null
}

/**
 * Whether the list keeps re-reading for this row: an audio file on its way, from the queue on.
 * A run starts its job at once, so its row is "analyzing" or "scheduled" before it is
 * "processing": without this the row would stay there until the page is loaded again.
 */
export function isAudioInFlight(document: StagedDocument): boolean {
  return document.status !== 'failed' && audioStageOf(document) !== null
}
